import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { SITE_GLASS_CARD } from '../constants/siteUi';
import { fetchPublishedResourceArticleBySlug } from '../lib/investorResourceApi';
import { RESOURCE_CATEGORY_LABEL, getResourceArticleBySlug, type ResourceArticle, type ResourceDifficulty } from '../data/investorResourceArticles';

function difficultyClass(d: ResourceDifficulty): string {
  switch (d) {
    case 'beginner':
      return 'bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30';
    case 'intermediate':
      return 'bg-amber-500/15 text-amber-200 ring-1 ring-amber-400/30';
    case 'advanced':
      return 'bg-rose-500/15 text-rose-200 ring-1 ring-rose-400/30';
    default:
      return 'bg-white/10 text-[var(--home-muted-on-dark)]';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function InvestorResourceArticle() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ResourceArticle | undefined>(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!slug) {
      setArticle(undefined);
      setReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const fromApi = await fetchPublishedResourceArticleBySlug(slug);
      if (cancelled) return;
      if (fromApi) setArticle(fromApi);
      else setArticle(getResourceArticleBySlug(slug));
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!ready) {
    return (
      <div className={`${SITE_GLASS_CARD} p-8 text-center font-body text-sm text-[var(--home-muted-on-dark)]`}>
        Loading article…
      </div>
    );
  }

  if (!article) {
    return (
      <div className={`${SITE_GLASS_CARD} p-8 text-center`}>
        <h1 className="font-display text-xl font-bold text-[var(--home-text-on-dark)]">Article not found</h1>
        <p className="mt-2 font-body text-sm text-[var(--home-muted-on-dark)]">
          That resource may have been moved or the link is incorrect.
        </p>
        <Link
          to="/dashboard/resources"
          className="mt-6 inline-block font-body text-sm font-semibold text-[var(--home-miami-blue)] underline-offset-4 hover:underline"
        >
          ← Back to knowledge base
        </Link>
      </div>
    );
  }

  const bodyMd = article.body?.trim();
  const bodyFallback =
    'This is a preview of our investor knowledge base. Full long-form guides and downloads can be wired to your CMS or editorial workflow when you are ready.';

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/resources"
        className="inline-flex items-center gap-2 font-body text-sm font-medium text-[var(--home-miami-blue)] underline-offset-4 hover:underline"
      >
        ← Back to knowledge base
      </Link>

      <article className={`${SITE_GLASS_CARD} p-6 md:p-8`}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <span className="rounded-full bg-[var(--home-miami-blue)]/20 px-2.5 py-0.5 font-body text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--home-miami-blue)]">
            {RESOURCE_CATEGORY_LABEL[article.category]}
          </span>
          <span
            className={`rounded-full px-2.5 py-0.5 font-body text-[0.65rem] font-semibold capitalize ${difficultyClass(article.difficulty)}`}
          >
            {article.difficulty}
          </span>
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--home-text-on-dark)] md:text-3xl">
          {article.title}
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-4 font-body text-xs text-[var(--home-muted-on-dark)]">
          <span className="inline-flex items-center gap-1">
            <svg className="size-3.5 shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {article.readMinutes} min read
          </span>
          <span className="inline-flex items-center gap-1">
            <svg className="size-3.5 shrink-0 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formatDate(article.publishedAt)}
          </span>
        </div>
        <p className="mt-6 font-body text-base leading-relaxed text-[var(--home-text-on-dark)]/90">{article.description}</p>
        <div className="mt-6 flex flex-wrap gap-2 border-t border-white/10 pt-6">
          {article.tags.map((t) => (
            <span
              key={t}
              className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 font-mono text-xs text-[var(--home-text-on-dark)]/85"
            >
              {t}
            </span>
          ))}
        </div>
        <div
          className={`mt-8 border-t border-white/10 pt-8 text-sm leading-relaxed text-[var(--home-text-on-dark)]/95 space-y-3 [&_p]:my-2 [&_strong]:text-[var(--home-text-on-dark)] [&_li]:my-1 [&_h1]:text-xl [&_h2]:text-lg [&_h2]:font-semibold`}
        >
          {bodyMd ? <ReactMarkdown>{bodyMd}</ReactMarkdown> : <p className="text-[var(--home-muted-on-dark)]">{bodyFallback}</p>}
        </div>
      </article>
    </div>
  );
}
